<template>
  <transition name="preview-fade">
    <div
      v-if="visible"
      class="preview-modal fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-3 sm:p-6"
      @click.self="close"
    >
      <div
        class="preview-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Предпросмотр документа"
      >
        <header class="preview-dialog__header">
          <div class="flex items-center gap-2.5 min-w-0">
            <h2 class="preview-dialog__title">Предпросмотр документа</h2>
          </div>
          <button
            type="button"
            class="preview-dialog__close"
            aria-label="Закрыть"
            @click="close"
          >
            <svg-icon name="huge-close" class="w-5 h-5 fill-none" />
          </button>
        </header>

        <div class="preview-dialog__body">
          <TipTapEditor2
            mode="preview"
            :with-border="false"
            :templateLang="localLang"
            :json="currentJson"
            :signatures="signatures"
            hide-pending-signature
            ref="editorRef"
          />
        </div>

        <footer class="preview-dialog__footer">
          <div
            class="mr-auto flex items-center gap-0.5 rounded-md bg-muted p-0.5"
          >
            <button
              v-for="loc in langs"
              :key="loc.value"
              type="button"
              class="px-2.5 py-1.5 text-xs rounded-md transition-colors"
              :class="
                loc.value === localLang
                  ? 'bg-background text-foreground shadow-sm font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              "
              @click="changeLang(loc.value)"
            >
              {{ loc.title }}
            </button>
          </div>
          <button type="button" class="preview-dialog__btn" @click="close">
            Закрыть
          </button>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import TipTapEditor2 from "./TipTapEditor2.vue";

export default {
  name: "ConfirmationModal",
  components: { TipTapEditor2 },
  props: {
    templateLang: {
      type: String,
      required: true,
    },
    templateJson: {
      type: Object,
      required: true,
    },
    contentByLang: {
      type: Object,
      default: null,
    },
    signatures: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      visible: false,
      localLang: this.templateLang,
      langs: [
        { value: "ru", title: "Рус" },
        { value: "kz", title: "Қаз" },
        { value: "en", title: "Eng" },
      ],
    };
  },
  computed: {
    currentJson() {
      const json = this.contentByLang?.[this.localLang];
      if (json) return JSON.parse(JSON.stringify(json));
      return this.templateJson;
    },
  },
  emits: ["close", "cancel", "confirm"],
  watch: {
    visible(open) {
      if (open) {
        document.addEventListener("keydown", this.onKeydown);
        document.body.style.overflow = "hidden";
      } else {
        document.removeEventListener("keydown", this.onKeydown);
        document.body.style.overflow = "";
      }
    },
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.onKeydown);
    document.body.style.overflow = "";
  },
  methods: {
    async open() {
      let resolve;
      let reject;
      const popupPromise = new Promise((ok, fail) => {
        resolve = ok;
        reject = fail;
      });
      this.$options.popupController = { resolve, reject };

      this.localLang = this.templateLang;
      this.visible = true;

      return popupPromise;
    },
    onKeydown(e) {
      if (e.key === "Escape") this.close();
    },
    close() {
      this.$options.popupController &&
        this.$options.popupController.resolve({
          action: "close",
        });
      this.visible = false;
    },
    reset() {
      this.visible = false;
    },
    reject() {
      this.close();
      this.reset();
    },
    confirm() {
      this.$options.popupController &&
        this.$options.popupController.resolve({
          action: "confirm",
        });
      this.reset();
    },
    changeLang(lang) {
      this.localLang = lang;
    },
  },
};
</script>

<style lang="scss">
.preview-fade-enter-active,
.preview-fade-leave-active {
  transition: opacity 0.18s ease;
}
.preview-fade-enter-from,
.preview-fade-leave-to {
  opacity: 0;
}
.preview-fade-enter-active .preview-dialog,
.preview-fade-leave-active .preview-dialog {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.preview-fade-enter-from .preview-dialog,
.preview-fade-leave-to .preview-dialog {
  transform: scale(0.97) translateY(8px);
  opacity: 0;
}

.preview-modal {
  .preview-dialog {
    @apply flex flex-col w-full overflow-hidden bg-background;
    max-width: calc(21cm + 5rem);
    height: 90vh;
    max-height: 900px;
    border-radius: var(--radius);
    box-shadow: 0 24px 48px -12px rgba(16, 24, 40, 0.28),
      0 0 0 1px rgba(16, 24, 40, 0.04);
  }

  .preview-dialog__header {
    @apply flex items-center justify-between gap-3 shrink-0 px-5 py-3.5 border-b border-border bg-muted/50;
  }

  .preview-dialog__title {
    @apply text-sm font-semibold truncate leading-none text-foreground;
  }

  .preview-dialog__close {
    @apply flex items-center justify-center w-9 h-9 shrink-0 text-muted-foreground transition-colors;
    border-radius: var(--radius);

    &:hover {
      @apply bg-muted text-foreground;
    }
  }

  .preview-dialog__body {
    @apply flex flex-col flex-1 min-h-0 p-4;
  }

  .preview-dialog__footer {
    @apply flex items-center justify-end gap-2 shrink-0 px-5 py-3 border-t border-border bg-muted/50;
  }

  .preview-dialog__btn {
    @apply px-4 py-2 text-sm font-medium bg-primary text-primary-foreground transition-opacity;
    border-radius: var(--radius);

    &:hover {
      opacity: 0.9;
    }
  }
}
</style>
